/**
 * Phase 45 Plan 45-08 (D-14) — TaskDetailSlideOver activity timeline section.
 *
 * Reads the latest 50 audit rows for the task across the task-scoped
 * SubjectType namespaces (including `task_state_change` Kanban state moves,
 * added by quick task 260727-ekn) and renders a human-readable,
 * chronologically sorted (DESC) feed.
 *
 * Event label derivation lives in `eventLabel()` — switches on
 * (subjectType, state) and decorates with payload fields like before/after,
 * itemId, etc. The full StateChange.changes payload shape is per-state and
 * is documented in backend/src/services/stateChangeService.ts:178-209.
 */
import { formatDistanceToNow } from 'date-fns';
import {
  useTaskActivity,
  type TaskActivityEvent,
} from '../../hooks/useTaskActivity';
import { formatHeldBy, parseHeldBy } from '../../utils/robotHold';
import { HeldByText } from '../common/HeldByText';
import { useRobotEnabled } from '../../hooks/useRobotEnabled'; // MKS-338

/**
 * MKS-338 — the two audit namespaces that exist only because of the robot /
 * Factory™ add-on: `task_routing` (MKS-241 Robot overlap holds, dispatch) and
 * `task_execution` (Phase 114 Factory™ run lifecycle). On an instance without
 * the add-on nothing writes them any more, but an instance that HAD it and
 * had it switched off still carries the history — and a row reading "handed to
 * the Robot" has no meaning there. Every other namespace is human activity and
 * stays.
 */
const ROBOT_SUBJECT_TYPES: readonly string[] = ['task_routing', 'task_execution'];

interface TaskActivityTimelineProps {
  taskId: number;
}

// Quick task 260809-qcy — resolution precedence for the actor name/initial:
// internal `user` first (unchanged), then the external actor resolved from
// `changes.actor` (see taskActivityRoutes.ts), then the "System" fallback
// for rows with no resolvable actor at all. The portal thread
// (RequestDetail.tsx activitySystemLine) resolves actors with the SAME
// precedence — a future edit to one should prompt an edit to the other.
function resolvedActorName(event: Pick<TaskActivityEvent, 'user' | 'externalActor' | 'agentName'>): string {
  // A Factory™ row names the builder agent that ran the task, not the system user.
  if (event.agentName) return event.agentName;
  if (event.user) {
    const parts = [event.user.firstName, event.user.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
    return event.user.email;
  }
  const ext = event.externalActor;
  if (ext) {
    const parts = [ext.firstName, ext.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
    return ext.email;
  }
  return 'System';
}

function actorName(event: Pick<TaskActivityEvent, 'user' | 'externalActor' | 'agentName'>): string {
  return resolvedActorName(event);
}

function actorInitial(event: Pick<TaskActivityEvent, 'user' | 'externalActor' | 'agentName'>): string {
  const name = resolvedActorName(event);
  const first = name.trim();
  return first.charAt(0).toUpperCase() || '?';
}

function payloadValue(
  changes: TaskActivityEvent['changes'],
  key: string,
): unknown {
  if (!changes || Array.isArray(changes)) return undefined;
  return (changes as Record<string, unknown>)[key];
}

function fmtBool(v: unknown): string {
  if (v === true) return 'done';
  if (v === false) return 'open';
  return '—';
}

// taskStateService's `project.task_state_changed` rows carry before/after
// OBJECTS ({ taskStateId, stateName }) unlike the Kanban writer's flat
// fromStateName/toStateName — pull the name out of either shape.
function stateNameFromPayload(v: unknown): string | null {
  if (typeof v === 'string') return v.length > 0 ? v : null;
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const name = (v as Record<string, unknown>).stateName;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

// Last-resort humanizer for events with no explicit branch: keep the row
// readable ("task state changed") instead of leaking the raw event key.
// The raw key stays available to admins via the row's title attribute.
function humanizeEventKey(state: string): string {
  const last = state.split('.').pop() ?? state;
  return last.replace(/_/g, ' ');
}

interface AssigneeUserPayload {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

function isAssigneeUserPayload(v: unknown): v is AssigneeUserPayload {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const r = v as Record<string, unknown>;
  return typeof r.id === 'number' && typeof r.email === 'string';
}

function nameFromPayload(u: AssigneeUserPayload): string {
  const parts = [u.firstName, u.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return u.email;
}

/** The repository named by a family event, when it carries one (plain text only). */
function familyRepository(changes: TaskActivityEvent['changes']): string | null {
  const repository = payloadValue(changes, 'repository');
  return typeof repository === 'string' && repository.length > 0 ? repository : null;
}

export function eventLabel(event: TaskActivityEvent): string {
  const actor = actorName(event);
  const { subjectType, state, changes } = event;

  // MKS-241 — Robot overlap holds.
  if (subjectType === 'task_routing') {
    if (state === 'routing.held') {
      return formatHeldBy(parseHeldBy(payloadValue(changes, 'heldBy')));
    }
    if (state === 'routing.hold_cleared') return 'No longer waiting; handed to the Robot';
    if (state === 'routing.hold_overridden') {
      return `${actor} dispatched this task anyway (hold overridden)`;
    }
    // MKS-365 — the feature went straight to the Robot because whoever filed it could have
    // approved it themselves. Without this the row would read "triager approval skipped",
    // which says what happened but not why.
    if (state === 'routing.triager_approval_skipped') {
      const capability = payloadValue(changes, 'creatorCapability');
      const filedBy =
        capability === 'admin'
          ? 'an admin'
          : capability === 'project_manager' || capability === 'product_manager'
            ? "one of the project's managers"
            : 'someone who can approve it';
      return `No approval needed — this ticket was filed by ${filedBy}`;
    }
  }

  if (subjectType === 'task_assignee' && state === 'assignee_changed') {
    // UAT 2026-05-22 #4 — backend taskActivityRoutes enriches the changes
    // payload with `beforeUser` / `afterUser` objects so we can show names
    // instead of "user 7". Legacy rows (pre-fix) still carry only numeric
    // `before` / `after`; we fall back to "someone" so the row remains
    // readable instead of leaking internal IDs.
    const beforeUser = payloadValue(changes, 'beforeUser');
    const afterUser = payloadValue(changes, 'afterUser');
    const before = payloadValue(changes, 'before');
    const after = payloadValue(changes, 'after');
    const actorId = event.user?.id ?? null;
    const beforeName = isAssigneeUserPayload(beforeUser)
      ? nameFromPayload(beforeUser)
      : 'someone';
    const afterName = isAssigneeUserPayload(afterUser)
      ? nameFromPayload(afterUser)
      : 'someone';
    if (before == null && after != null) {
      // Self-claim vs. PM-assigns-someone-else.
      return actorId != null && actorId === after
        ? `${actor} claimed this task`
        : `${actor} assigned this task to ${afterName}`;
    }
    if (before != null && after == null) {
      return actorId != null && actorId === before
        ? `${actor} dropped the assignment`
        : `${actor} unassigned ${beforeName}`;
    }
    return `${actor} reassigned this task from ${beforeName} to ${afterName}`;
  }

  if (subjectType === 'task_assignee' && state === 'author_approved') {
    return `${actor} approved this ticket`;
  }

  if (subjectType === 'task_assignee' && state === 'author_reopened') {
    // Quick task 260810-l18 — author review. The state move itself gets its
    // own task_state_change row, so this label carries only the decision +
    // reason.
    const reason = payloadValue(changes, 'reason');
    return typeof reason === 'string' && reason.length > 0
      ? `${actor} reopened this ticket: “${reason}”`
      : `${actor} reopened this ticket`;
  }

  if (subjectType === 'task_acceptance') {
    if (state === 'acceptance_toggled') {
      const after = payloadValue(changes, 'after');
      return `${actor} marked an acceptance item ${fmtBool(after)}`;
    }
    if (state === 'acceptance_added') return `${actor} added an acceptance item`;
    if (state === 'acceptance_removed') return `${actor} removed an acceptance item`;
    if (state === 'acceptance_text_updated')
      return `${actor} edited an acceptance item`;
    if (state === 'acceptance_replaced')
      return `${actor} replaced the acceptance list`;
  }

  if (subjectType === 'task_priority' && state === 'priority_changed') {
    const after = payloadValue(changes, 'after');
    return `${actor} set priority to ${String(after ?? '—')}`;
  }

  // MKS-164 — a dedicated one-liner is a clarity improvement, not a
  // correctness fix: the existing humanizeEventKey fallback below already
  // keeps this row readable for an unknown namespace.
  if (subjectType === 'task_labels' && state === 'labels_changed') {
    const before = payloadValue(changes, 'before');
    const after = payloadValue(changes, 'after');
    const beforeCount = Array.isArray(before) ? before.length : 0;
    const afterCount = Array.isArray(after) ? after.length : 0;
    const added = Math.max(afterCount - beforeCount, 0);
    const removed = Math.max(beforeCount - afterCount, 0);
    if (added > 0 && removed === 0) return `${actor} added ${added} label${added === 1 ? '' : 's'}`;
    if (removed > 0 && added === 0)
      return `${actor} removed ${removed} label${removed === 1 ? '' : 's'}`;
    return `${actor} changed the labels`;
  }

  if (subjectType === 'task_lifecycle') {
    if (state === 'milestone_changed') return `${actor} changed the milestone`;
    if (state === 'task_lifecycle.created') return `${actor} created this task`;
    if (state === 'task_lifecycle.updated') return `${actor} updated this task`;
    if (state === 'task_lifecycle.deleted') return `${actor} deleted this task`;
  }

  if (subjectType === 'task') {
    if (state === 'attachment_uploaded') return `${actor} uploaded an attachment`;
    if (state === 'attachment_deleted') return `${actor} removed an attachment`;
  }

  if (subjectType === 'task_state_change' && state === 'task_state_change.applied') {
    // Quick task 260727-ekn — Kanban state moves. fromStateName is null when
    // the task came from Triage (no prior state).
    const fromStateName = payloadValue(changes, 'fromStateName');
    const toStateName = payloadValue(changes, 'toStateName');
    if (typeof fromStateName === 'string' && fromStateName.length > 0) {
      return `${actor} moved this task from ${fromStateName} to ${String(toStateName ?? '—')}`;
    }
    return `${actor} moved this task to ${String(toStateName ?? '—')}`;
  }

  if (
    subjectType === 'task' &&
    (state === 'project.task_state_changed' || state === 'status_changed')
  ) {
    // Two more state-move writers besides the Kanban one:
    // taskStateService.setTaskState (`project.task_state_changed`, object
    // payloads) and the claim auto-transition in taskAssigneeService
    // (`status_changed`, plain-string payloads).
    const beforeName = stateNameFromPayload(payloadValue(changes, 'before'));
    const afterName = stateNameFromPayload(payloadValue(changes, 'after'));
    if (beforeName) {
      return `${actor} moved this task from ${beforeName} to ${afterName ?? '—'}`;
    }
    return `${actor} moved this task to ${afterName ?? '—'}`;
  }

  if (subjectType === 'task_assignee' && state === 'external_approved') {
    return `${actor} approved this ticket`;
  }
  if (subjectType === 'task_assignee' && state === 'external_rejected') {
    const reason = payloadValue(changes, 'reason');
    return typeof reason === 'string' && reason.length > 0
      ? `${actor} rejected this ticket: “${reason}”`
      : `${actor} rejected this ticket`;
  }

  if (subjectType === 'task_intake') {
    // Phase 51 INTAKE-04 rows (client portal intake flow).
    if (state === 'client_submitted') return `${actor} submitted this request`;
    if (state === 'triage_assigned') return `${actor} triaged this task`;
    if (state === 'triage_moved_to_open')
      return `${actor} moved this task from Triage to Open`;
  }

  if (subjectType === 'task_execution') {
    // Phase 114 (Plan 114-04 / PCEXEC-01, D-114-01) — this is the ONLY place
    // a human sees run lifecycle events; they never become TaskComment rows.
    // These rows have no human actor, so `actor` resolves to the existing
    // "System" fallback unchanged (see resolvedActorName above) — no new
    // actor-resolution path is added here.
    // Deliberately NOT enumerated: 'execution.event_ignored_stale' and
    // 'execution.comment_suppressed_echo' — no readable sentence exists for
    // either, so they fall through to the humanized fallback below, which is
    // the correct, honest behaviour for a diagnostic row (the raw key stays
    // available via the row's own `title` attribute).
    if (state === 'execution.run_started') return `${actor} ${runStartedPhrase(event.id)}`;
    if (state === 'execution.run_finished') return `${actor} finished running on this task`;
    if (state === 'execution.run_failed') return `${actor} failed while running on this task`;
    if (state === 'execution.run_cancelled') return `${actor} cancelled the run on this task`;
    if (state === 'execution.issue_updated') return `${actor} updated the linked Factory™ issue`;
    if (state === 'execution.comment_landed') return `${actor} posted a comment via Factory™`;

    // MKS-312 (Plan 126-05) — one readable sentence per family state.
    // 'execution.family_synced' and 'execution.family_member_status' stay on the fallback: they
    // are bookkeeping rows with nothing a person needs to read.
    const repository = familyRepository(changes);
    if (state === 'execution.family_completed') {
      return `${actor} completed the Factory™ change across all repositories`;
    }
    if (state === 'execution.done_deferred_family') {
      return `${actor} held Done until every repository is deployed`;
    }
    if (state === 'execution.family_deployed') {
      return repository
        ? `${actor} recorded the change deployed in ${repository}`
        : `${actor} recorded a repository change as deployed`;
    }
    if (state === 'execution.family_pr_merged') {
      return repository
        ? `${actor} recorded the pull request merged in ${repository}`
        : `${actor} recorded a pull request as merged`;
    }
    if (state === 'execution.family_failed') {
      return `${actor} marked the Factory™ change as failed`;
    }
    if (state === 'execution.family_member_off_allowlist') {
      return `${actor} flagged a Factory™ issue for a repository that is not on the allowed list`;
    }
    if (state === 'execution.family_ui_review_held') {
      return `${actor} held the UI review until every repository is deployed`;
    }
  }

  // Fallback for unknown events: humanized wording, never the raw event key
  // (UAT 2026-08-13 — raw `task.project.task_state_changed` leaked into the
  // timeline). The raw key is still exposed via the title attribute below.
  return `${actor} · ${humanizeEventKey(state)}`;
}

// Heavy-work phrasing for a run start. Picked by the event id, so a row keeps
// its wording across reloads while different runs read differently.
const RUN_STARTED_PHRASES = [
  'started crunching this task',
  'rolled up its sleeves on this task',
  'started hammering away at this task',
  'started grinding through this task',
  'dug into this task',
  'started wrestling with this task',
  'started chewing through this task',
  'got its hands dirty on this task',
  'started churning through this task',
  'put its shoulder to this task',
] as const;

export function runStartedPhrase(eventId: number): string {
  return RUN_STARTED_PHRASES[Math.abs(eventId) % RUN_STARTED_PHRASES.length]!;
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function TaskActivityTimeline({ taskId }: TaskActivityTimelineProps) {
  const { data: allEvents = [], isLoading, isError } = useTaskActivity(taskId);
  const robotEnabled = useRobotEnabled();
  // MKS-338 — drop the robot-only namespaces when the add-on is off. The
  // "Showing the latest 50 events" note below keys off this filtered length,
  // which is correct: it is a statement about what the person can see.
  const data = robotEnabled
    ? allEvents
    : allEvents.filter((event) => !ROBOT_SUBJECT_TYPES.includes(event.subjectType));

  return (
    <section
      aria-label="Activity"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="activity-timeline-section"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary mb-2">
        Activity
      </h3>

      {isLoading ? (
        <p className="text-sm text-ms-text-muted italic">Loading activity…</p>
      ) : isError ? (
        <p className="text-sm text-ms-text-muted italic">Couldn&apos;t load activity.</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No activity yet.</p>
      ) : (
        <ul className="space-y-2" data-testid="activity-list">
          {data.map((event) => (
            <li
              key={event.id}
              className="flex items-start gap-2"
              data-testid={`activity-row-${event.id}`}
            >
              <div
                className="shrink-0 h-7 w-7 rounded-full bg-ms-canvas border border-ms-border-subtle flex items-center justify-center text-[11px] font-semibold text-ms-text"
                aria-hidden="true"
              >
                {actorInitial(event)}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm text-ms-text"
                  title={`${event.subjectType}.${event.state}`}
                >
                  {event.subjectType === 'task_routing' && event.state === 'routing.held' ? (
                    <HeldByText heldBy={parseHeldBy(payloadValue(event.changes, 'heldBy'))} />
                  ) : (
                    eventLabel(event)
                  )}
                </p>
                <p className="text-xs text-ms-text-secondary">
                  {relativeTime(event.createdAt)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {data.length === 50 && (
        <p className="mt-2 text-xs text-ms-text-secondary italic">
          Showing the latest 50 events.
        </p>
      )}
    </section>
  );
}
